import { ReactElement, MouseEvent, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload as AddOrdersIcon,
  faChevronDown as KeyboardArrowDownIcon,
} from '@fortawesome/free-solid-svg-icons';

import { useAppSelector } from '~hooks/useAppSelector';
import { useToggleMenu } from '~hooks/useToggleMenu';
import { selectAuth } from '~store/slices/auth/index';
import currentTheme from '~config/theme';
import { isOnlyReadUser } from '~utils/user';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { PATHS } from '~constants/paths';
import AddOrdersActions from '~components/AddOrdersActions';

import {
  DashboardVariantV2GiverStatusInfoWidgetCard,
  DashboardVariantV2GiverWidgetActionButton,
} from '../../SubComponents';

import { useDashboardVariantV2GiverContext } from '../../../Context';
import { DashboardVariantV2GiverContextTabId } from '../../../types';

const WIDGET_TAB_ID: DashboardVariantV2GiverContextTabId = 'inPreparation';
const WIDGET_ACCENT_COLOR = currentTheme.palette.quaternary.main;

const DashboardVariantV2GiverInPreparationWidget = (): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

  const {
    activeTab: { getIsActiveTab, setActiveTab },
    statistics: { data: statistics },
    dialogs: {
      importFromFile: { toggleOpen: toggleOpenDialogImportFromFile },
      importFromIntegration: {
        toggleOpen: toggleOpenDialogImportFromIntegration,
      },
    },
  } = useDashboardVariantV2GiverContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    setActiveTab(WIDGET_TAB_ID);
  }, [setActiveTab]);

  const [anchorElAddOrdersMenu, openAddOrdersMenu, toggleOpenAddOrdersMenu] =
    useToggleMenu();

  const handleRedirectToNewOrder = useCallback(() => {
    navigate(PATHS.ORDERS.NEW);
  }, [navigate]);

  const handleOpenAddOrdersMenu = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      toggleOpenAddOrdersMenu();
    },
    [toggleOpenAddOrdersMenu],
  );

  return (
    <>
      <DashboardVariantV2GiverStatusInfoWidgetCard
        accentColor={WIDGET_ACCENT_COLOR}
        isSelected={isSelected}
        onSelected={handleSelectedTab}
        header={{
          statistics: {
            current: statistics.inPreparationItems,
            total: statistics.totalItems,
          },
          icon: {
            src: toAbsoluteUrl('/icons/icon_section_preparation.svg'),
            alt: 'Section Preparation Icon',
          },
          title: {
            text: 'EN PREPARACIÓN',
            tooltip: 'Esperando que el transportista retire las órdenes.',
          },
          withDivider: false,
        }}
        content={
          <>
            {!isUserOnlyReader && (
              <DashboardVariantV2GiverWidgetActionButton
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={AddOrdersIcon} />}
                endIcon={<FontAwesomeIcon icon={KeyboardArrowDownIcon} />}
                fullWidth
                size="large"
                ref={anchorElAddOrdersMenu}
                onClick={handleOpenAddOrdersMenu}
              >
                Agregar pedidos
              </DashboardVariantV2GiverWidgetActionButton>
            )}
          </>
        }
      />

      <AddOrdersActions.Menu
        anchorEl={anchorElAddOrdersMenu.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={openAddOrdersMenu}
        onClose={toggleOpenAddOrdersMenu}
        items={{
          manual: {
            onAction: handleRedirectToNewOrder,
          },
          fromFile: {
            onAction: () => toggleOpenDialogImportFromFile(true),
          },
          fromIntegration: {
            onAction: () => toggleOpenDialogImportFromIntegration(true),
          },
        }}
      />
    </>
  );
};

export default DashboardVariantV2GiverInPreparationWidget;
